import React from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';
import { pageMetadata } from '@/lib/seo/metadata';
import { PublicContainer } from '@/components/layouts/PublicContainer';

const LAST_UPDATED = 'July 12, 2026';

const TermsOfService: React.FC = () => {
    usePageSEO(pageMetadata(PUBLIC_PAGES.legalTerms));
    return (
        <div className="bg-background py-24 sm:py-32">
            <PublicContainer>
                <div className="mx-auto max-w-3xl">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Terms of Service</h1>
                    <p className="mt-2 text-lg leading-8 text-muted-foreground">Last updated: {LAST_UPDATED}</p>

                    <div className="mt-10 space-y-8 text-base leading-7 text-muted-foreground">
                        <p>
                            DebtDrone is operated by an independent solo founder ("we," "us," or "our"). By accessing or using DebtDrone, you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the service.
                        </p>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">1. Use of Service</h2>
                            <p>
                                DebtDrone provides automated technical debt analysis. You agree to use the service only for lawful purposes and in accordance with these Terms.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">2. Payment Processing</h2>
                            <p>
                                All payments for DebtDrone subscriptions are processed by <strong className="text-foreground">Paddle.com Market Limited</strong>, which acts as the merchant of record. Paddle handles payment processing, tax collection, and invoicing for all transactions. By subscribing, you also agree to Paddle's own terms of use as they apply to the checkout and billing process.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">3. Subscription Plans</h2>
                            <ul className="list-disc pl-5 mt-4 space-y-2">
                                <li><strong>Free/Starter Plan:</strong> Limited to 3 active repositories and 10 scans per month. Provided "as is" with no SLA.</li>
                                <li><strong>Pro Plan:</strong> Allows up to 100 active repositories and unlimited scans. Includes priority email support.</li>
                                <li><strong>Enterprise Plan:</strong> Custom repository limits, SSO/SAML integration, and on-premise deployment options. Covered by a separate MSA/SLA.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">4. Fair Use & Limitations</h2>
                            <p>
                                We reserve the right to throttle or terminate accounts that abuse the API or exceed reasonable usage limits, even on unlimited plans. "Unlimited" is defined as usage consistent with normal business operations of a single engineering team.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">5. Disclaimers</h2>
                            <p>
                                DebtDrone is a static analysis tool. While we strive for accuracy, we do not guarantee that our analysis will find every issue or that our suggestions are error-free. You should review all code changes manually.
                            </p>
                            <p className="mt-4">
                                THE SERVICE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">6. Limitation of Liability</h2>
                            <p>
                                In no event shall DebtDrone, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">7. Changes</h2>
                            <p>
                                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide at least 30 days' notice prior to any new terms taking effect.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">8. Governing Law</h2>
                            <p>
                                These Terms are governed by and construed in accordance with the laws of Albania, without regard to its conflict of law principles. Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Albania.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground mt-10 mb-4">9. Contact</h2>
                            <p>Questions about these Terms can be sent to:</p>
                            <div className="mt-4 rounded-xl border border-border bg-card p-5">
                                <p>
                                    <strong className="text-foreground">Email:</strong>{' '}
                                    <a href="mailto:support@debtdrone.net" className="text-foreground underline underline-offset-2 hover:opacity-70 transition-opacity">
                                        support@debtdrone.net
                                    </a>
                                </p>
                            </div>
                        </section>
                    </div>
                </div>
            </PublicContainer>
        </div>
    );
};

export default TermsOfService;
